// Which drums run together, and in what order they reach the machine.
//
// ⚠ DUPLICATED BYTE-FOR-BYTE at eng-fontend-v1/src/helpers/groupBy.js — CRA
// cannot import from outside src/. tests/groupBy.test.js fails the moment the
// two copies differ, because the Final Production Review PREDICTS this ordering
// before the launch writes it: a preview that groups differently from the queue
// is worse than no preview.
//
// ── two different things, often confused ───────────────────────────────────
//
// MERGE EXECUTION puts several products' shares on ONE drum — one token, one
// pass. That pairing is `helpers/mergeSlot.js`.
//
// GROUP BY is only an ORDER. The drums stay separate tokens belonging to
// separate products; they are merely enqueued so that everything of the same
// colour (or the same wire Ø) runs back to back, because changing the colour on
// an extruder costs a purge and changing the wire costs a re-thread. A
// group-by-colour launch of a CYYF 3x2.5 and a CYYF 4x2.5 is therefore
// blue+blue, brown+brown, green-yellow+green-yellow, and the 4x2.5's black
// alone — not three drums of one product followed by four of the other.
//
// `unwinding` groups by wire Ø instead, because that is what a pay-off changes.

/**
 * The ordering key for one task, or null when its phase is not grouped.
 *
 * `GB::<phase>::<kind>::<value>`. The queue display splits this back apart
 * (`machineQueueBlocks.js`: prefix = the first three parts, so the whole run is
 * one bracket whatever the colour), so the shape is a contract, not a detail.
 *
 * @param phaseName    the phase the task belongs to
 * @param mode         "color" | "wireDiameter" | falsy — `groupByPhases[phase]`
 * @param color        the task's colour (isolation colour at conductor_isolation)
 * @param wireDiameter the wire Ø, for the "wireDiameter" mode
 */
export function groupByKeyOf({ phaseName, mode, color, wireDiameter } = {}) {
  if (!mode || !phaseName) return null;

  if (mode === "color") {
    return `GB::${phaseName}::color::${String(color || "").toUpperCase()}`;
  }

  if (mode === "wireDiameter") {
    // `Number(null)` is 0 and `Number("")` is 0, so a drum whose Ø is simply not
    // known would bucket as "Ø0" — a diameter no cable has — while a drum whose
    // field is absent buckets as "NA". The same two unknowns have to land in the
    // same bucket, so they are made the same kind of unknown first.
    const wd =
      wireDiameter === null || wireDiameter === undefined || wireDiameter === ""
        ? NaN
        : Number(wireDiameter);

    return `GB::${phaseName}::wd::${Number.isFinite(wd) ? wd : "NA"}`;
  }

  return null;
}

/**
 * Which bucket a pending queue insertion belongs to.
 *
 * Three kinds, and the order of the tests matters: a MERGED entry buckets by
 * its shared token even when its phase is also grouped, because the merge is a
 * physical fact (one drum) and the grouping is only a preference.
 *
 * A solo entry gets a bucket of its own, keyed on its position, so it stays
 * exactly where the planner put it.
 */
export function queueBucketKeyOf(entry, idx) {
  if (entry?.mergeQueueKey) return `M::${entry.machineId}::${entry.mergeQueueKey}`;
  if (entry?.groupByKey) return `G::${entry.machineId}::${entry.groupByKey}`;
  return `S::${entry?.machineId}::${idx}`;
}

/**
 * The order the entries are enqueued in: bucket by bucket, each bucket in the
 * position where it was FIRST seen, and the entries inside a bucket in the order
 * the planner listed them.
 *
 * First-seen rather than sorted by key, deliberately: the planner's order still
 * decides which colour runs first. Sorting the keys would quietly re-order a
 * launch alphabetically — black before blue, whatever the planner intended.
 *
 * @returns a new array; the input is not mutated
 */
export function orderByBucket(entries = []) {
  const indexed = (entries || []).map((entry, idx) => ({
    entry,
    idx,
    key: queueBucketKeyOf(entry, idx),
  }));

  const firstSeen = new Map();
  for (const item of indexed) {
    if (!firstSeen.has(item.key)) firstSeen.set(item.key, item.idx);
  }

  indexed.sort((a, b) =>
    a.key === b.key ? a.idx - b.idx : firstSeen.get(a.key) - firstSeen.get(b.key)
  );

  return indexed.map((item) => item.entry);
}

export default groupByKeyOf;
